import React from 'react';
import { Button } from '../Button';
import { Badge } from '../Badge';
type Bubble = {
  from: 'lead' | 'business' | 'system';
  text: string;
  time?: string;
};
function ChatThread({
  bubbles
}: {
  bubbles: Bubble[];
}) {
  return <div className="flex flex-col gap-2 p-4" data-id="element-29">
      {bubbles.map((b, i) => {
      if (b.from === 'system') {
        return <div key={i} className="text-center text-xs italic text-gray-500 py-1" data-id="element-30">
              — {b.text} —
            </div>;
      }
      const isLead = b.from === 'lead';
      return <div key={i} className={`flex ${isLead ? 'justify-start' : 'justify-end'}`} data-id="element-31">
            <div className={`max-w-[80%] border border-gray-300 rounded-lg px-3 py-2 text-sm ${isLead ? 'bg-gray-100 text-gray-900' : 'bg-gray-800 text-white'}`} data-id="element-32">
              <div data-id="element-33">{b.text}</div>
              {b.time && <div className={`text-[10px] mt-1 ${isLead ? 'text-gray-500' : 'text-gray-300'}`} data-id="element-34">
                  {b.time}
                </div>}
            </div>
          </div>;
    })}
    </div>;
}
const withoutBubbles: Bubble[] = [{
  from: 'lead',
  text: "Hi, my kitchen sink is leaking badly. Can someone come out today? It's an emergency.",
  time: '9:12 AM'
}, {
  from: 'system',
  text: '23 minutes pass'
}, {
  from: 'business',
  text: 'Hi! Yes, we can help. When works for you?',
  time: '9:35 AM'
}, {
  from: 'system',
  text: 'Sarah is already talking to a competitor.'
}, {
  from: 'business',
  text: 'Hello? Are you still there?',
  time: '12:18 PM'
}, {
  from: 'business',
  text: '[Voicemail left]',
  time: '12:20 PM'
}, {
  from: 'business',
  text: 'Just following up — did you still need help?',
  time: '4:02 PM'
}];
const withBubbles: Bubble[] = [{
  from: 'lead',
  text: "Hi, my kitchen sink is leaking badly. Can someone come out today? It's an emergency.",
  time: '9:12 AM'
}, {
  from: 'system',
  text: 'AI texts Sarah within 8 seconds'
}, {
  from: 'business',
  text: 'Hi Sarah — I see your request just came in. We can have a plumber out today between 11am and 1pm. Does that work?',
  time: '9:12 AM'
}, {
  from: 'lead',
  text: 'Yes! That would be amazing.',
  time: '9:13 AM'
}, {
  from: 'business',
  text: "Perfect. You're booked. We'll text you a confirmation and the tech's name shortly.",
  time: '9:13 AM'
}, {
  from: 'system',
  text: 'Appointment confirmed · Total time: 80 seconds'
}];
function ChatCard({
  label,
  bubbles,
  resultLeft,
  resultRight
}: {
  label: string;
  bubbles: Bubble[];
  resultLeft: string;
  resultRight: string;
}) {
  return <div className="border border-gray-300 rounded-2xl bg-white flex flex-col overflow-hidden" data-id="element-35">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50" data-id="element-36">
        <div className="flex items-center gap-2" data-id="element-37">
          <div className="w-8 h-8 border border-gray-300 bg-gray-100 rounded-md flex items-center justify-center text-[10px] italic text-gray-500" data-id="element-38">
            logo
          </div>
          <div data-id="element-39">
            <div className="text-sm font-semibold text-gray-900" data-id="element-40">
              Your Business
            </div>
            <div className="text-xs text-gray-500" data-id="element-41">Today · 9:12 AM</div>
          </div>
        </div>
        <Badge variant="secondary" size="small" data-id="element-42">
          <span className="text-[11px] font-medium" data-id="element-43">{label}</span>
        </Badge>
      </div>

      <div className="flex-1 min-h-[420px] bg-white" data-id="element-44">
        <ChatThread bubbles={bubbles} data-id="element-45" />
      </div>

      <div className={`px-4 py-3 flex items-center justify-between border-t border-gray-200 ${label === 'Speed to Lead AI' ? 'bg-navy-500 text-white' : 'bg-gray-900 text-white'}`} data-id="element-46">
        <span className="text-sm font-semibold" data-id="element-47">{resultLeft}</span>
        <span className="text-sm text-gray-300" data-id="element-48">{resultRight}</span>
      </div>
    </div>;
}
export function ChatComparison() {
  return <section className="w-full border-b border-gray-200 bg-white px-4 sm:px-6 lg:px-12 py-12 sm:py-20 lg:py-24" data-id="element-49">
      <div className="mx-auto max-w-[1280px]" data-id="element-50">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 text-center max-w-2xl mx-auto mb-10 sm:mb-14" data-id="element-51">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-navy-500 text-white uppercase tracking-[0.15em] text-[11px] font-semibold" data-id="element-52">
            Your Unfair Advantage
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-gray-900 tracking-tight leading-[1.1]" data-id="element-53">
            See the difference speed makes.
          </h2>
          <p className="text-base text-gray-600 leading-relaxed" data-id="element-54">
            Same lead. Same form submission. Two very different outcomes.
          </p>
        </div>

        {/* 3-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px_1fr] gap-8 items-stretch" data-id="element-55">
          <ChatCard label="Without speed-to-lead" bubbles={withoutBubbles} resultLeft="Lead lost" resultRight="3+ hours" data-id="element-56" />

          {/* Center CTA */}
          <div className="flex flex-col items-center justify-center gap-4 text-center px-2" data-id="element-57">
            <h3 className="text-xl lg:text-2xl font-semibold text-gray-900 leading-tight" data-id="element-58">
              Ready to win every lead?
            </h3>
            <Button variant="primary" size="large" rightIcon={<span aria-hidden data-id="element-60">→</span>} className="!bg-navy-500 !border-navy-500 hover:!bg-navy-800 hover:!border-navy-800" data-id="element-59">
              Book a Demo
            </Button>
            <p className="text-xs text-gray-500 leading-relaxed" data-id="element-61">
              Free 15-minute walkthrough
              <br data-id="element-62" />
              No credit card required
            </p>
          </div>

          <ChatCard label="Speed to Lead AI" bubbles={withBubbles} resultLeft="Deal won" resultRight="80 seconds" data-id="element-63" />
        </div>
      </div>
    </section>;
}